// Enhanced mixin for sub-entities with on-demand hierarchy loading
import {
  get_headers,
  LIST_SUB_ENTITIES_BY_PARENT_APPLICATION,
} from "@/utils/constants/constants.js"; 
import axios from "axios";

export const sub_entities_mixin = {
  data:() =>({
    displayed_applications_list: [],
      sanitized_pending_applications_list: [],
      application_row_map: {},
      table_headers: [],
      profile_role_ids: [],
      pending_applications_list: [],
      hierarchy_loading: false,
  }),
  methods: {
    /**
     * Get sub-entities for a parent application (non-recursive - matches FolderList expectations)
     */
    async get_sub_entities(parent_application_id, entity_profile_id) {
      if (typeof this.show_loader !== 'function' || typeof this.close_loader !== 'function') {
        return [];
      }

      const loader = await this.show_loader("Loading sub-entities...");

      try {
        const params = { parent_application_id: Number(parent_application_id) };
        if (Number.isInteger(entity_profile_id) && entity_profile_id > 0) {
          params.entity_profile_id = Number(entity_profile_id);
        }

        const api_url = LIST_SUB_ENTITIES_BY_PARENT_APPLICATION;
        
        const response = await axios.get(api_url, {
          headers: this.headers || (this.headers = get_headers()),
          params,
        });

        const body = typeof response.data === "string" ? JSON.parse(response.data) : response.data;        
        if (body?.status !== "success") {
          if (loader) await this.close_loader(loader);
          return [];
        }

        const sub_entities = Array.isArray(body?.data) ? body.data : (body?.data?.children || []);
        
        if (loader) await this.close_loader(loader);
        return sub_entities || [];

      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || "failed to load sub_entities";
        this.show_error(msg);
        if (loader) await this.close_loader(loader);
        return [];
      }
    },

    /**
     * Load hierarchy for current application - LOAD ROOT ITEM WITH DIRECT CHILDREN
     */
    async load_full_hierarchy() {
      if (!this.application_id) {
        return;
      }
      
      if (this.hierarchy_loading) {
        return;
      }
      
      this.hierarchy_loading = true;
      
      try {        
        // First, check if this application has any children
        const has_children = await this.check_if_has_children_direct(this.application_id, this.entity_profile_id);
        
        // Create root entity
        const root_entity = {
          id: this.application_id,
          entity_type: {
            id: this.entity_type_id || 0,
            entity_type_name: this.entity_name || 'Root Entity'
          },
          profile_role: [],
          entity_name: this.entity_name || 'Root Entity',
          date_created: new Date().toISOString(),
          status: { status: this.application_status || 'Unknown' },
          entity: this.entity_profile_id,
          attributes: [],
          // IMPORTANT: We need to set these correctly
          has_children: has_children,
          _children_loaded: false, // Children not loaded yet
          _children_checked: true, // We've checked for children
          children: has_children ? [] : [] // Start with empty array if has children
        };

        // Load immediate children if exists (first level only)
        if (has_children) {
          const children = await this.get_sub_entities(this.application_id, this.entity_profile_id);
          root_entity.children = children.map(child => 
            this.make_node_from_child_entity(child, this.application_id)
          );
          root_entity._children_loaded = true;
          root_entity._children_count = children.length;
        }

        this.pending_applications_list = [root_entity];
        this.prep_tables();
        
      } catch (error) {
        this.show_error('Failed to load entity hierarchy: ' + error.message);
      } finally {
        this.hierarchy_loading = false;
      }
    },

    /**
     * Check if an item has children without loading them
     */
    async check_if_has_children_direct(parent_application_id, entity_profile_id) {
      return this.get_sub_entities(parent_application_id, entity_profile_id)
        .then(children => children && children.length > 0)
        .catch(() => false);
    },

    /**
     * Enhanced fetch_assigned_for_item - This is called by FolderList when expanding items
     */
    async fetch_assigned_for_item(item) {
      if (!this.is_valid_item(item)) return;

      const parent_app_id = this.extract_application_id(item);
      if (!parent_app_id) return;

      // For child items, use their own ID as entity_profile_id
      const entity_profile_id = item.is_child ? Number(item.id) : this.entity_profile_id;

      if (item.is_loading) return;
      item.is_loading = true;

      try {
        const children = await this.get_sub_entities(parent_app_id, entity_profile_id);
        this.assign_children_to_item(item, children, parent_app_id);
        this.prep_tables(); // Call prep_tables to refresh the display
      } catch (err) {
        this.mark_item_as_failed(item);
        this.prep_tables(); // Still refresh display even on error
      } finally {
        item.is_loading = false;
      }
    },

    is_valid_item(item) {
      return !!item;
    },

    extract_application_id(item) {
      return Number(item["Application No"] ?? item.application_id ?? item.id);
    },

    assign_children_to_item(item, children, parent_app_id) {
      item.children = (children || []).map(child =>
        this.make_node_from_child_entity(child, parent_app_id)
      );
      item._children_count = item.children.length;
      item._children_checked = true;
      item.has_children = item.children.length > 0;
      item._children_loaded = true;
    },

    mark_item_as_failed(item) {
      item._children_checked = true;
      item.has_children = false;
      item._children_loaded = true;
    },

    /**
     * Enhanced method to load sub-entities (for backward compatibility)
     */
    async load_sub_entities() {
      await this.load_full_hierarchy();
    },

    /**
     * Main method to prepare data for FolderList component
     */
    prep_tables() {
      if (!this.pending_applications_list || this.pending_applications_list.length === 0) {
        this.reset_table_state();
        return;
      }

      this.sanitized_pending_applications_list = [];
      this.application_row_map = {};

      // Map to preserve expanded state
      const expandedStateMap = {};
      if (this.displayed_applications_list && this.displayed_applications_list.length > 0) {
        this.displayed_applications_list.forEach(item => {
          if (item._key) {
            expandedStateMap[item._key] = item.expanded || false;
          }
        });
      }

      this.sanitized_pending_applications_list = this.pending_applications_list.map(application => {
        const profile_role_id = this.get_valid_profile_role_id(application.id);
        this.map_application_row(application, profile_role_id);
        return this.format_application(application, profile_role_id);
      });

      // Preserve expanded state
      this.displayed_applications_list = this.sanitized_pending_applications_list.map(item => {
        const expanded = expandedStateMap[item._key] || false;
        return {
          ...item,
          expanded: expanded,
          _children_checked: !!item._children_checked,
          _children_loaded: !!item._children_loaded,
          is_loading: false,
        };
      });

      this.table_headers = this.sanitized_pending_applications_list.length > 0
        ? Object.keys(this.sanitized_pending_applications_list[0])
        : [];
    },

    reset_table_state() {
      this.table_headers = [];
      this.displayed_applications_list = [];
      this.sanitized_pending_applications_list = [];
      this.application_row_map = {};
    },

    map_application_row(application, profile_role_id) {
      this.application_row_map[application.id] = {
        ...application,
        profile_role_id,
        entity_profile_id: application.entity,
      };
    },

    format_application(application) {
      return this._build_application_payload(application);
    },

    _build_application_payload(app) { return {...this._extract_base_fields(app), ...this._extract_meta_fields(app)}; },

    _extract_base_fields(app) {
      const get_default = (value, default_value) => value !== undefined ? value : default_value;
      
      const base_fields = {
        id: app.id,
        type_id: app.entity_type?.id ?? null,
        entity_profile_id: app.entity,
        children: get_default(app.children, []),
        expanded: get_default(app.expanded, false),
        is_child: get_default(app.is_child, false),
        has_children: get_default(app.has_children, false),
        _children_loaded: get_default(app._children_loaded, false),
        _children_checked: get_default(app._children_checked, false),
        is_loading: false,
        _key: `app-${app.id}`,
        _attributes: Array.isArray(app.attributes) ? app.attributes : []
      };
      
      return base_fields;
    },

    _extract_meta_fields(app) {
      const entity_type_name = app.entity_type?.entity_type_name ?? "Entity";
      const status = app.status?.status;
      const formatted_status = this.format_question_type ? this.format_question_type(status) : status;
      
      // Enhanced user ID extraction
      let user_id = "N/A";
      let user_name = "N/A";
      
      // Try multiple ways to extract user info
      if (app.profile_role && Array.isArray(app.profile_role) && app.profile_role.length > 0) {
        const profile = app.profile_role[0];
        
        // Check different possible structures
        if (profile.user?.id) {
          user_id = profile.user.id;
          user_name = `${profile.user.first_name || ''} ${profile.user.last_name || ''}`.trim() || profile.user.name || "N/A";
        } else if (profile.user_id) {
          user_id = profile.user_id;
        }
      }
      
      // Fallback: check app directly for user info
      if (user_id === "N/A" && app.user_id) {
        user_id = app.user_id;
      }
      
      // For child entities, check _child_raw
      if (user_id === "N/A" && app._child_raw) {
        const child = app._child_raw;
        if (child.user_id) {
          user_id = child.user_id;
          user_name = child.user_name || "N/A";
        } else if (child.profile_role && Array.isArray(child.profile_role)) {
          const profile = child.profile_role[0];
          if (profile?.user?.id) {
            user_id = profile.user.id;
            user_name = `${profile.user.first_name || ''} ${profile.user.last_name || ''}`.trim();
          }
        }
      }
      
      const meta_fields = { 
        "Application No": app.id, 
        "Entity Name": this.get_entity_name(app), 
        "Entity Type": entity_type_name, 
        "Date Created": app.date_created, 
        "User ID": user_id, 
        "User Name": user_name, // Add user name for debugging
        Status: formatted_status 
      };
      
      return meta_fields;
    },

    /**
     * Extract entity name from application data
     */
    get_entity_name(application) {
      if (!application) return "entity not yet onboarded";
      const user_name = `${application?.profile_role?.[0]?.user?.first_name ?? ""} ${application?.profile_role?.[0]?.user?.last_name ?? ""}`.trim();
      const entity_name = application?.entity_name?.trim();
      
      return user_name || entity_name || "entity not yet onboarded";
    },

    /**
     * Get valid profile role ID
     */
    get_valid_profile_role_id(application_id) {
      if (!this.profile_role_ids || !Array.isArray(this.profile_role_ids)) {
        return null;
      }

      const profile_role_entry = this.profile_role_ids.find(
        (entry) => entry.application_id === application_id
      );

      return profile_role_entry?.roleId &&
        Number.isInteger(profile_role_entry.roleId) &&
        profile_role_entry.roleId > 0
        ? profile_role_entry.roleId
        : null;
    },

    /**
     * Normalize item for consistent data structure
     */
    normalize_item(item) {
      if (!item) {
        const default_item = {
          is_child: false,
          type_id: 0,
          application_id: 0,
          entity_profile_id: 0,
          entity_type_name: "Entity",
        };
        return default_item;
      }

      const is_child = Boolean(item.is_child);
      
      const type_id_value = item.type_id ?? item.entity_type_id ?? item._child_raw?.entity_type_id ?? 0;
      const type_id = Number(type_id_value);
      
      const application_id_value = item["Application No"] ?? item.application_id ?? item._child_raw?.forms?.[0]?.application_id ?? 0;
      const application_id = Number(application_id_value);
      
      let entity_profile_id_value;
      if (is_child) {
        entity_profile_id_value = item.id ?? item.entity_profile_id ?? 0;
      } else {
        entity_profile_id_value = item.entity_profile_id ?? item.id ?? 0;
      }
      const entity_profile_id = Number(entity_profile_id_value);
      
      const entity_type_name_value = item["Entity Type"] ?? item.entity_type_name ?? item._child_raw?.entity_type_name ?? "Entity";
      const entity_type_name = entity_type_name_value;

      const normalized_item = {
        is_child,
        type_id,
        application_id,
        entity_profile_id,
        entity_type_name,
      };
      
      return normalized_item;
    },

    /**
     * Create child node from child entity data - UPDATED for proper hierarchy
     */
    make_node_from_child_entity(child, parent_app_id) {
      if (!child) {
        return this.create_empty_child_node(parent_app_id);
      }

      const application_id = Array.isArray(child.forms) && child.forms.length
        ? Number(child.forms[0].application_id)
        : Number(parent_app_id) || 0;

      const name = child.entity_name || child.name || "Sub-entity not yet onboarded";
      const created_at = Array.isArray(child.forms) && child.forms[0]
        ? (child.forms[0].date_created || "")
        : (child.date_created || "");

      // Check if this child might have its own children
      const has_children = Boolean(child.has_children || child.child_count > 0 || 
        (Array.isArray(child.forms) && child.forms[0]?.has_children));

      const node = {
        id: Number(child.id),
        entity_profile_id: Number(child.id),
        application_id,
        entity_type_id: Number(child.entity_type_id),
        entity_type_name: child.entity_type_name || "Entity",
        is_child: true,
        has_children: has_children,
        children: [], // Start with empty children array
        expanded: false,
        Status: child.status?.status || child.forms?.[0]?.status || "",
        _child_raw: child,
        _children_checked: has_children ? false : true, // Only need to check if might have children
        _children_loaded: false, // Not loaded yet
        is_loading: false,
        _attributes: this.normalize_child_attributes(child),
        "Application No": application_id,
        "Entity Name": name,
        "Entity Type": child.entity_type_name || "Entity",
        "Date Created": created_at,
        _key: `child-${child.id}-${application_id}`
      };

      node._show_all_attrs = false;      
      return node;
    },

    /**
     * Create empty child node as fallback
     */
    create_empty_child_node(parent_app_id) {
        const application_id = Number(parent_app_id) || 0;
        
        const empty_child_node = {
          id: 0,
          entity_profile_id: 0,
          application_id: application_id,
          entity_type_id: 0,
          entity_type_name: "Entity",
          is_child: true,
          has_children: false,
          children: [],
          expanded: false,
          Status: "",
          _child_raw: null,
          _children_checked: true,
          _children_loaded: true,
          is_loading: false,
          _attributes: [],
          "Application No": application_id,
          "Entity Name": "Invalid sub-entity",
          "Entity Type": "Entity",
          "Date Created": "",
          _key: `child-0-${application_id}`
        };
        
        return empty_child_node;
      },

    /**
     * Normalize child attributes
     */
    normalize_child_attributes(child) {
      if (!child) return [];

      if (Array.isArray(child?.attributes)) return child.attributes;

      const f0 = Array.isArray(child?.forms) ? child.forms[0] : null;
      if (f0?.attributes && Array.isArray(f0.attributes)) return f0.attributes;

      if (Array.isArray(f0?.answers)) {
        return f0.answers.map(a => ({
          attribute_id: a.question_id,
          attribute_name: a.question_text,
          attribute_type: a.type,
          value: a.value,
          has_value: a.value != null && String(a.value).trim() !== '',
          entity_type_attribute_id: a.entity_type_attribute_id ?? a.question_id,
        }));
      }

      return [];
    },

    /**
     * Check if item has children (for manual checking)
     */
    async check_if_has_children(item) {
      if (!item) return;

      const parent_app_id = Number(item["Application No"] ?? item.application_id ?? item.id);
      if (!parent_app_id) {
        item.has_children = false;
        return;
      }

      const entity_profile_id = item.is_child ? Number(item.id) : undefined;

      try {
        const children = await this.get_sub_entities(parent_app_id, entity_profile_id);
        item.has_children = children && children.length > 0;
        item._children_checked = true;
      } catch (err) {
        item.has_children = false;
        item._children_checked = true;
      }
    },

    handle_check_children(item) {
      this.check_if_has_children(item);
    },

    check_all_items_for_children() {
      if (this.displayed_applications_list && this.displayed_applications_list.length > 0) {
        this.displayed_applications_list.forEach(item => {
          if (!item._children_checked && !item.is_child) {
            this.check_if_has_children(item);
          }
        });
      }
    },

    /**
     * Clear all FolderList data
     */
    clear_folder_list_data() {
      this.displayed_applications_list = [];
      this.sanitized_pending_applications_list = [];
      this.application_row_map = {};
      this.table_headers = [];
      this.pending_applications_list = [];
    },

    /**
     * Refresh hierarchy data
     */
    async refresh_hierarchy() {
      this.clear_folder_list_data();
      await this.load_full_hierarchy();
    },
  },
  computed: {
    /**
     * Get count of applications by status
     */
    get_count() {
      return (status) =>
        this.sanitized_pending_applications_list.filter(
          (app) => app.Status === status
        ).length;
    },
    /**
     * Get total applications count
     */
    total_applications_count() {
      return this.sanitized_pending_applications_list.length;
    },

    /**
     * Get displayed applications count
     */
    displayed_applications_count() {
      return this.displayed_applications_list.length;
    },
    /**
     * Check if hierarchy is currently loading
     */
    is_hierarchy_loading() {
      return this.hierarchy_loading;
    }
  }
};